import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Observable, map } from "rxjs";
import { environment } from "../../../environments/environment";
import { CreateFreeformRequest, FreeformResponse, UpdateFreeformRequest } from "../models/freeform.model";
import { FreeformTagsResponse } from "../models/freeform-tags.model";
import { FreeformSegmentsResponse } from "../submissions/models/submission-freeform.model";
import { FreeformId, stripPrefix } from "../../shared/models/entity.model";

// Backend response omits the `type` discriminator (the endpoint implies it). We tag it
// client-side so a fetched Freeform can participate in `Poll | Freeform` narrowing.
type FreeformResponseBody = Omit<FreeformResponse, "type">;

@Injectable({ providedIn: "root" })
export class FreeformApiService {
    private readonly http = inject(HttpClient);
    private readonly baseUrl = `${environment.interactiveExperienceServiceUrl}/v1/interactives/freeform`;

    get(id: FreeformId): Observable<FreeformResponse> {
        return this.http
            .get<FreeformResponseBody>(`${this.baseUrl}/${stripPrefix(id)}`)
            .pipe(map(response => ({ ...response, type: "freeform" })));
    }

    create(request: CreateFreeformRequest): Observable<FreeformResponse> {
        return this.http
            .post<FreeformResponseBody>(this.baseUrl, request)
            .pipe(map(response => ({ ...response, type: "freeform" })));
    }

    update(id: string, request: UpdateFreeformRequest): Observable<FreeformResponse> {
        return this.http
            .patch<FreeformResponseBody>(`${this.baseUrl}/${id}`, request)
            .pipe(map(response => ({ ...response, type: "freeform" })));
    }

    /** Permanently deletes a freeform interactive (`InteractiveFreeformController.delete`). */
    delete(id: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/${id}`);
    }

    getTags(interactiveIds: readonly string[]): Observable<FreeformTagsResponse> {
        return this.http.get<FreeformTagsResponse>(`${this.baseUrl}/tags`, {
            params: { interactiveIds }
        });
    }

    getSegments(interactiveIds: readonly string[]): Observable<FreeformSegmentsResponse> {
        return this.http.get<FreeformSegmentsResponse>(`${this.baseUrl}/segments`, {
            params: { interactiveIds }
        });
    }
}
